import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import MapOutlined from '@mui/icons-material/MapOutlined';
import { ParticipantVideoGallery } from './ParticipantVideo';
import { useCallTiles, type CallTileLayoutProps } from './callTileProps';
import { CameraOffPicture, remoteCameraOffPicture } from '../cameraOffPicture';
import {
  galleryCapacity,
  galleryLayout,
  galleryVisibleSlots,
  splitGalleryTiles,
} from '../utils/galleryLayout';
import {
  GalleryCell,
  GalleryGridArea,
  GalleryHeader,
  GalleryOverflowLabel,
  GalleryOverflowPictures,
  GalleryOverflowTile,
  GalleryRoot,
} from '../styles/GalleryVideoDisplay.styles';

interface GalleryVideoDisplayProps extends CallTileLayoutProps {
  /** Leaves the gallery for the layout used before it. */
  onBackToMap: () => void;
  /** Camera tiles on screen, for the subscribed video quality; null once the gallery closes. */
  onVisibleTileCountChange?: (count: number | null) => void;
}

/** Faces drawn on the "+N others" tile; the rest are only counted. */
const OVERFLOW_PICTURES = 4;

interface AreaSize {
  width: number;
  height: number;
}

/**
 * SC-375: the Gallery display mode — everyone in your conversation as equal tiles covering the map,
 * with you as one of them (alone, your tile fills the area). The grid geometry and the screen-size
 * cap come from `galleryLayout`; who is shown, and in which order, is shared with the sidebar.
 */
export const GalleryVideoDisplay: React.FC<GalleryVideoDisplayProps> = ({
  onBackToMap,
  onVisibleTileCountChange,
  ...layout
}) => {
  const { participants } = layout;
  const gridRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState<AreaSize>({ width: 0, height: 0 });

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => {
      const next = { width: grid.clientWidth, height: grid.clientHeight };
      setArea((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  const measured = area.width > 0 && area.height > 0;
  const capacity = useMemo(() => galleryCapacity(area), [area]);

  const getViewportCapacity = useCallback(
    (tileCount: number) => (measured ? galleryVisibleSlots(tileCount, capacity) : 0),
    [measured, capacity]
  );

  const { order, tileProps } = useCallTiles(layout, getViewportCapacity);

  const { visible, overflow } = splitGalleryTiles(order, capacity);
  const tileCount = visible.length + (overflow.length > 0 ? 1 : 0);
  const { tileWidth, tileHeight } = galleryLayout({ count: tileCount, ...area, capacity });

  // Unmeasured, the grid "fits" one tile; reporting that would briefly lift every camera in a large talk.
  useEffect(() => {
    if (measured) onVisibleTileCountChange?.(visible.length);
  }, [onVisibleTileCountChange, measured, visible.length]);

  useEffect(() => () => onVisibleTileCountChange?.(null), [onVisibleTileCountChange]);

  const overflowParticipants = overflow
    .map((id) => participants.find((p) => p.identity === id))
    .filter(Boolean);

  const cellStyle = { width: tileWidth, height: tileHeight };

  return (
    <GalleryRoot data-testid="gallery-video-display">
      <GalleryHeader>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<MapOutlined fontSize="small" />}
          onClick={onBackToMap}
          data-testid="gallery-back-to-map"
        >
          Back to map
        </Button>
      </GalleryHeader>
      <GalleryGridArea ref={gridRef}>
        {tileWidth > 0 && visible.map((participantId) => {
          const participant = participants.find((p) => p.identity === participantId);
          if (!participant) return null;
          return (
            <GalleryCell key={`gallery-${participant.isLocal ? 'local' : 'remote'}-${participantId}`} style={cellStyle}>
              <ParticipantVideoGallery {...tileProps(participant)} />
            </GalleryCell>
          );
        })}
        {tileWidth > 0 && overflow.length > 0 && (
          <GalleryCell key="gallery-overflow" style={cellStyle}>
            <GalleryOverflowTile data-testid="gallery-overflow">
              <GalleryOverflowPictures>
                {overflowParticipants.slice(0, OVERFLOW_PICTURES).map((p: any) => (
                  <CameraOffPicture
                    key={p.identity}
                    picture={remoteCameraOffPicture(p)}
                    size="48px"
                  />
                ))}
              </GalleryOverflowPictures>
              <GalleryOverflowLabel>
                +{overflow.length} {overflow.length === 1 ? 'other' : 'others'}
              </GalleryOverflowLabel>
            </GalleryOverflowTile>
          </GalleryCell>
        )}
      </GalleryGridArea>
    </GalleryRoot>
  );
};
